import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Row, Col, Button, Spinner, ProgressBar } from 'react-bootstrap';
import { AlertaService } from '../../../services/apiService';
import Swal from 'sweetalert2';
import { toast } from "react-toastify";
import withReactContent from 'sweetalert2-react-content';
import Filtros from "./Filtros";
import ParadasNoAutorizadas from "./components/paradas-no-autorizadas-total/ParadasNoAutorizadasTotal";
import ParadasNoAutorizadasVehiculo from "./components/paranas-no-autorizadas-vehiculo/ParadasNoAutorizadasVehiculo";
import NoAutorizadosMasterDetailList from "./components/paradas-no-autorizadas-listado/NoAutorizadosMasterDetailList";
import cargarPaginas from '../../../providers/provider_paginado';
import { paginadorAlertasStore } from '../../../lib/store/paginador_store';
import { jwtDecode } from 'jwt-decode';
import Flex from '../../../components/common/Flex';
import classNames from 'classnames';
import PrintReportModule from '../../../components/print/PrintReportModule';
import { LoadingEmptyState, NoDataEmptyState, WelcomeEmptyState } from '../../../components/common/EmptyState';
import StandardProgressBar from '../../../components/common/ProgressBar/StandardProgressBar';

export default function paradasNoAutorizadas() {

    const paginadorAlertas = paginadorAlertasStore();

    // Añadir este estado para el progreso de carga
    const [totalCarga, setTotalCarga] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [isLoading, setIsLoading] = useState(false); // Estado para controlar el spinner

    const [loadingComponents, setLoadingComponents] = useState({
        table: { loading: false, progress: 0, complete: false },
        vehicles: { loading: false, progress: 0, complete: false }
    });

    const calculateTotalProgress = useCallback(() => {
        const weights = {
            table: 0.4,      // ✅ Ajustar para que sume 100%
            vehicles: 0.3,   // ✅ Solo componentes que existen
            alerts: 0.3,     // ✅ Solo componentes que existen                
        };

        let totalProgress = 0;
        let totalWeight = 0;

        Object.entries(loadingComponents).forEach(([component, state]) => {
            if (state && (state.loading || state.complete)) {
                const progress = Number(state.progress);
                if (!isNaN(progress)) {
                    totalProgress += progress * weights[component];
                    totalWeight += weights[component];
                } else {
                    totalProgress += (state.complete ? 100 : 0) * weights[component];
                    totalWeight += weights[component];
                }
            }
        });

        if (totalWeight === 0) return 0;
        const result = totalProgress / totalWeight;

        return Math.min(Math.max(result, 0), 100);
    }, [loadingComponents]);

    const [debugMetrics, setDebugMetrics] = useState({
        searchStartTime: null,
        searchEndTime: null,

        connections: {
            'ListTable': {
                name: 'Bitacora (GPS)',
                startTime: null,
                endTime: null,
                totalRecordsExpected: 0,
                totalRecordsReceived: 0,
                totalApiCalls: 0,
                completedApiCalls: 0,
                failedApiCalls: 0,
                recoveredApiCalls: 0,
                apiCallsHistory: [],
                downloadProgress: 0,
                status: 'pending'
            }
        },

        globalTotalRecordsExpected: 0,
        globalTotalRecordsReceived: 0,
        globalDownloadProgress: 0,
        activeConnections: 0,

        componentTimes: {
            table: { start: null, end: null },
            vehicles: { start: null, end: null },
            alerts: { start: null, end: null },
            map: { start: null, end: null },
            frequency: { start: null, end: null }
        }
    });

    const updateComponentProgress = useCallback((component, progress, isComplete = false) => {
        setLoadingComponents(prevState => {
            const newState = { ...prevState };

            if (newState[component]) {
                newState[component] = {
                    ...newState[component],
                    loading: !isComplete,
                    progress: isComplete ? 100 : Math.max(0, Math.min(100, progress)),
                    complete: isComplete
                };
            }

            return newState;
        });

        // ✅ Actualizar métricas directamente sin dependencia circular
        setDebugMetrics(prev => ({
            ...prev,
            componentTimes: {
                ...prev.componentTimes,
                [component]: {
                    start: !isComplete ? Date.now() : (prev.componentTimes[component]?.start || Date.now()),
                    end: isComplete ? Date.now() : null
                }
            }
        }));
    }, []);

    const updateDebugMetrics = useCallback((updates) => {
        setDebugMetrics(prev => {
            if (!prev || !prev.connections) {
                console.warn('⚠️ debugMetrics no está inicializado correctamente');
                return prev || {
                    connections: {},
                    globalTotalRecordsExpected: 0,
                    globalTotalRecordsReceived: 0,
                    globalDownloadProgress: 0,
                    activeConnections: 0,
                    componentTimes: {}
                };
            }
            const newState = { ...prev };

            if (updates.connectionId && newState.connections[updates.connectionId]) {
                const connection = newState.connections[updates.connectionId];

                if (!connection.apiCallsHistory) {
                    connection.apiCallsHistory = [];
                }

                // Actualizar datos específicos de la conexión
                Object.keys(updates).forEach(key => {
                    if (key !== 'connectionId') {
                        if (key === 'apiCallsHistory' && Array.isArray(updates[key])) {
                            const newCalls = updates[key].map(call => ({
                                ...call,
                                timestamp: call.timestamp || Date.now()
                            }));
                            connection[key] = [...(connection[key] || []), ...newCalls];

                            if (connection[key].length > 50) {
                                connection[key] = connection[key].slice(-50);
                            }
                        } else if (key !== 'apiCallsHistory') {
                            connection[key] = updates[key];
                        }
                    }
                });

                const activeConnections = Object.values(newState.connections).filter(conn =>
                    conn && (conn.status === 'downloading' || conn.status === 'completed')
                );

                newState.globalTotalRecordsExpected = activeConnections.reduce((sum, conn) =>
                    sum + (conn.totalRecordsExpected || 0), 0
                );

                newState.globalTotalRecordsReceived = activeConnections.reduce((sum, conn) =>
                    sum + (conn.totalRecordsReceived || 0), 0
                );

                newState.globalDownloadProgress = newState.globalTotalRecordsExpected > 0 ?
                    (newState.globalTotalRecordsReceived / newState.globalTotalRecordsExpected * 100) : 0;

                newState.activeConnections = activeConnections.length;

            } else if (updates.connectionId && !newState.connections[updates.connectionId]) {
                newState.connections[updates.connectionId] = {
                    name: updates.connectionId,
                    startTime: null,
                    endTime: null,
                    totalRecordsExpected: 0,
                    totalRecordsReceived: 0,
                    totalApiCalls: 0,
                    completedApiCalls: 0,
                    failedApiCalls: 0,
                    recoveredApiCalls: 0,
                    apiCallsHistory: [],
                    downloadProgress: 0,
                    status: 'pending'
                };

                Object.keys(updates).forEach(key => {
                    if (key !== 'connectionId') {
                        newState.connections[updates.connectionId][key] = updates[key];
                    }
                });

            } else {
                Object.keys(updates).forEach(key => {
                    if (key === 'componentTimes') {
                        if (!newState.componentTimes) {
                            newState.componentTimes = {};
                        }
                        newState.componentTimes = { ...newState.componentTimes, ...updates.componentTimes };
                    } else if (key !== 'connectionId') {
                        newState[key] = updates[key];
                    }
                });
            }

            return newState;
        });

        // ✅ SINCRONIZAR DESCARGA CON BARRA DE PROGRESO
        if (updates.connectionId && updates.downloadProgress !== undefined) {
            setTimeout(() => {
                const componentMapping = {
                    'ListTable': 'list'
                };

                const componentName = componentMapping[updates.connectionId];

                if (componentName) {
                    // ✅ Usar el progreso real de descarga para actualizar el componente
                    const isComplete = updates.downloadProgress >= 100;
                    const progressValue = Math.round(updates.downloadProgress || 0);

                    console.log(`🔄 [Sincronización] ${componentName}: ${progressValue}% (descarga ${isComplete ? 'completa' : 'en progreso'})`);

                    updateComponentProgress(componentName, progressValue, isComplete);

                    // ✅ Si la descarga está completa, también marcar como cargando = false
                    if (isComplete) {
                        setLoadingComponents(prevState => ({
                            ...prevState,
                            [componentName]: {
                                loading: false,
                                progress: 100,
                                complete: true
                            }
                        }));
                    }
                }
            }, 0);
        }
    }, [updateComponentProgress]);

    const [filters, setFilters] = useState({
        fecha_inicio: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
        fecha_fin: new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
        uids_vehiculo: [],
        uids_evento: [], //para el caso de omisiones
        tiempo: 0,
    });

    // 🚀 NUEVOS ESTADOS para resaltar 
    const [highlightedParadaNoAuth, setHighlightedParadaNoAuth] = useState(null);
    const [highlightedAlertType, setHighlightedAlertType] = useState(null);

    // 📌 NUEVOS ESTADOS PARA FILTRADO DINÁMICO
    const [selectedParadaNoAuth, setSelectedParadaNoAuth] = useState(null);
    const [selectedTitleParadaNoAuth, setSelectedTitleParadaNoAuth] = useState(null);
    const [selectedAlertType, setSelectedAlertType] = useState(null);

    // Mapa para asignar colores a cada tipo de alerta
    const alertColorsRef = useRef(new Map());
    const usedColors = new Set();
    const [searchValue, setSearchValue] = useState('');

    const getUniqueColor = () => {
        let newColor;
        do {
            newColor = `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`;
        } while (usedColors.has(newColor)); // Asegurar que el color no esté en uso
        usedColors.add(newColor);
        return newColor;
    };

    const getAlertColor = (alertType) => {
        if (!alertColorsRef.current.has(alertType)) {
            const newColor = getUniqueColor();
            alertColorsRef.current.set(alertType, newColor);
        }
        return alertColorsRef.current.get(alertType);
    };
    const EmptyState = ({ icon, title, message, isLoading }) => (
        <Card className="mt-3 text-center py-5">
            <Card.Body className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '40vh' }}>
                {isLoading ? (
                    <>
                        <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem', marginBottom: '1.5rem' }} />
                        <h4 className="text-primary mb-3">Cargando datos...</h4>
                        <p className="text-muted mb-0 w-75">Estamos procesando tu solicitud, esto puede tomar unos momentos.</p>
                    </>
                ) : (
                    <>
                        <div className="mb-3 text-muted" style={{ fontSize: '4rem' }}>
                            {icon || <i className="far fa-calendar-alt"></i>}
                        </div>
                        <h4 className="mb-3">{title}</h4>
                        <p className="text-muted mb-0 w-75">{message}</p>
                    </>
                )}
            </Card.Body>
        </Card>
    );


    const generarFiltro = (filtro) => {
        const decoded = jwtDecode(localStorage.getItem('token'));

        return {
            fecha_inicio: filtro.fecha_inicio,
            fecha_fin: filtro.fecha_fin,
            // Para paradas no autorizadas se usa uids_evento
            ids_tipo_alerta: filtro.ids_tipo_alerta || filtro.uids_evento,
            uids_vehiculo: filtro.uids_vehiculo,
            empresaUid: decoded.uid_empresa,
            page: 1,
            limit: 10000,
            uid_usuario: decoded.uid_usuario,
            perfil: 1,
            count: true,
            tiempo: filtro.tiempo || 0
        };
    };


    const handleGenerateReport = (selectedFilters) => {
        // Reiniciar paginador
        paginadorAlertas.editarDatos([]);
        paginadorAlertas.editarProgresoCargado(0);
        setTotalCarga([]);
        setFilters(selectedFilters);

        // Reiniciar alertas
        setAlerts([]);
    };

    useEffect(() => {
        const loadAlertsReport = async (filtros) => {
            //handleClearFilters();

            // Solo activar loading si hay vehículos seleccionados
            if (filtros.uids_vehiculo.length > 0) {
                setIsLoading(true);

                // ✅ ESTABLECER TIEMPO DE INICIO SI NO ESTÁ YA ESTABLECIDO
                setDebugMetrics(prev => ({
                    ...prev,
                    searchStartTime: prev.searchStartTime || Date.now(),
                    searchEndTime: null
                }));

                // ✅ MARCAR COMPONENTE COMO CARGANDO AL INICIAR DESCARGA
                setLoadingComponents(prevState => ({
                    ...prevState,
                    table: { loading: true, progress: 0, complete: false }
                }));

                updateComponentProgress('table', 10, false);

                try {
                    if (paginadorAlertas.cargando === false) {
                        cargarPaginas(
                            `${process.env.REACT_APP_ALERTS_API_URL}/alertsV2/obtener-reporte-notificaciones-v2`,
                            paginadorAlertas,
                            generarFiltro(filtros)
                        );
                    }
                } catch (error) {
                    console.error("Error al cargar eventos omisiones:", error);
                    MySwal.fire({
                        icon: 'error',
                        text: 'Error al procesar la solicitud',
                        showConfirmButton: true
                    });
                    // Desactivar loading en caso de error
                    setIsLoading(false);

                    // ✅ Completar carga de tabla
                    updateComponentProgress('table', 100, true);
                }
            }
        };

        loadAlertsReport(filters);
    }, [filters]);

    useEffect(() => {
        if (!window.printReportModule) {
            window.printReportModule = new PrintReportModule({
                captureQuality: 0.95,
                pdfFormat: 'A4',
                pdfOrientation: 'landscape',
                useNativeTables: true,
                maxRowsPerPage: 30,
                tableFontSize: 7,

                // ✅ CONFIGURACIÓN ESPECÍFICA PARA PARADAS NO AUTORIZADAS
                reportType: 'paradas-no-autorizadas',
                captureContainer: '#reporte-paradas-content',

                tableExtractionConfig: {
                    enabled: true,
                    selector: '#tabla-paradas',
                    title: '-'
                },

                // 🔧 CONFIGURACIÓN DE TABLA TANSTACK CORRECTA
                // tableExtractionConfig: {
                //   enabled: true,
                //   selector: '#card-listado-paradas',
                //   title: 'Listado de Paradas No Autorizadas',
                //   type: 'tanstack', // ✅ ES TANSTACK, NO HTML
                //   headerSelector: 'thead th', // Del HTML generado por Tanstack
                //   bodySelector: 'tbody tr',   // Del HTML generado por Tanstack
                //   cellSelector: 'td',         // Del HTML generado por Tanstack
                //   // ✅ Transformaciones para botones específicos
                //   cellTransforms: {
                //     'button .fa-map-marker-alt': 'Ver Ubicación',
                //     'button.btn-light': 'Ver Mapa'
                //   }
                // },

                // 📊 ESTRATEGIA HORIZONTAL: Corregida para usar selectores correctos
                horizontalStrategy: 'rows',
                horizontalRows: [
                    {
                        title: 'Gráficos de Análisis',
                        selector: '#reporte-paradas-content .row:first-child', // ✅ Primera fila
                        identifier: 'paradas-fila-1',
                        selectorMethod: 'direct'
                    },
                    //   {
                    //     title: 'Listado de Paradas No Autorizadas',
                    //     selector: '#card-listado-paradas', // ✅ Directamente la card con la tabla
                    //     identifier: 'paradas-fila-2',
                    //     selectorMethod: 'direct'
                    //   }
                ],

                // 📱 ESTRATEGIA VERTICAL: IDs específicos
                verticalStrategy: 'targets',
                captureTargets: [
                    {
                        title: 'Total de Paradas No Autorizadas',
                        selector: '#card-detail-paradas',
                        type: 'card'
                    },
                    {
                        title: 'Top 10 Vehículos - Paradas No Autorizadas',
                        selector: '#card-paradas-vehiculo',
                        type: 'card'
                    },
                    //   {
                    //     title: 'Listado Detallado de Paradas',
                    //     selector: '#card-listado-paradas',
                    //     type: 'card'
                    //   }
                ]
            });
        }

        return () => {
            if (window.printReportModule) {
                delete window.printReportModule;
            }
        };
    }, []);


    // Agregar este useEffect para actualizar los datos cuando el paginador cambie
    useEffect(() => {
        // Cuando termina la carga (ya sea con o sin datos)
        if (paginadorAlertas.cargando === false && isLoading) {
            if (paginadorAlertas.datos.length > 0) {

                //Nuevo Formato para el listado del vehiculo (Alias / No Economico)
                const dataVehicleFormat = [...(paginadorAlertas.datos.map(alert => {
                    let vehiculo = filters.vehiculos.find(item => item.value === alert.id_vehiculo);
                    let newAlert = {
                        ...alert,
                        no_economico: `${vehiculo.no_economico} / ${vehiculo.alias}`,
                        tipo_vehiculo: vehiculo.tipo_vehiculo,
                        color_vehiculo: vehiculo.color_vehiculo
                    };
                    return newAlert;
                }))];

                console.log('datos vehiculo encontrado', dataVehicleFormat);

                setAlerts(dataVehicleFormat);
            } else {
                // Si la carga terminó pero no hay datos, mostrar mensaje
                if (filters.uids_vehiculo.length > 0) {
                    /*MySwal.fire({
                        icon: 'info',
                        text: 'No se encontraron registros para la consulta',
                        showConfirmButton: true
                    });*/
                }
            }
            // Siempre desactivar loading cuando termina la carga
            setIsLoading(false);

            // ✅ Completar carga de tabla
            updateComponentProgress('table', 100, true);
        }

        if (paginadorAlertas.progresoCargado > 0) {

            // Actualizar el indicador de progreso
            updateComponentProgress('table', paginadorAlertas.progresoCargado, false);

            setTotalCarga([
                {
                    title: 'Total de reporte cargado',
                    percent: paginadorAlertas.progresoCargado,
                    color: "#2770e9",
                    animated: true
                }
            ]);
        }
    }, [paginadorAlertas, isLoading, filters.uids_vehiculo.length]);


    // Cargar alertas basadas en los filtros seleccionados
    // useEffect(() => {
    //     //Metodo para Listar todas las alertas del reporte segun el tipo de reporte
    //     const loadAlertsReport = async (filtros) => {
    //         handleClearFilters();
    //         setIsLoading(true); // Activar el spinner antes de la petición
    //         try {

    //             if (filtros.uids_vehiculo.length > 0) {
    //                 const response = await AlertaService.getAlertaReporteOmisiones(filtros);
    //                 if (response && response.statusCode === 200) {
    //                     setAlerts(response.data);

    //                     if (response.data.length <= 0) {

    //                         MySwal.fire({
    //                             icon: 'error',
    //                             text: 'No se encontraron registros para la consulta',
    //                             showConfirmButton: true
    //                         })
    //                     }
    //                 } else {
    //                     toast.error(response?.message || "Error al generar reporte");
    //                 }
    //             }
    //         } catch (error) {
    //             console.error("Error al cargar eventos omisiones:", error);
    //         } finally {
    //             setIsLoading(false); // Desactivar el spinner después de la petición
    //         }
    //     };
    //     loadAlertsReport(filters);
    // }, [filters]);

    const MySwal = withReactContent(Swal);

    // 📌 Método para seleccionar un evento en la gráfica "Total de paradas no autorizadas"
    const handleSelectParadaNoAuth = (event, title) => {

        setSelectedParadaNoAuth(prevEvent => prevEvent === event ? null : event); // 🔹 Si ya está seleccionado, lo deselecciona
        setHighlightedParadaNoAuth(prevEvent => prevEvent === event ? null : event);
        setSelectedTitleParadaNoAuth(prevEvent => prevEvent === title ? null : title);
        setSelectedAlertType(null); // 🔹 Limpiar la alerta seleccionada
        setHighlightedAlertType(null);
    };

    // 📌 Método para limpiar todos los filtros
    const handleClearFilters = () => {
        setSelectedAlertType(null);
        setHighlightedAlertType(null);
    };

    // 📌 Método para limpiar todos los filtros
    const handleClearFiltersNoAuth = () => {
        setSelectedParadaNoAuth(null);
        setHighlightedParadaNoAuth(null);
        setSelectedTitleParadaNoAuth(null);
    };

    // 📌 Método para seleccionar un vehiculo en la gráfica "Top 10 Vehiculos"
    const handleSelectAlertType = (alertType) => {
        setSelectedAlertType(alertType);
        setHighlightedAlertType(alertType);
    };

    // 📌 Aplicar los filtros según la selección de eventos en las graficas
    let filteredAlerts = alerts;
    let filteredAlertsByEvent = alerts;

    if (selectedParadaNoAuth) {
        filteredAlerts = filteredAlerts.filter(alert => alert.tipo_alerta === selectedParadaNoAuth);
        filteredAlertsByEvent = filteredAlertsByEvent.filter(alert => alert.tipo_alerta === selectedParadaNoAuth); // 🔹 Aplicar filtro por evento a la gráfica de vehiculos
    }

    if (selectedAlertType) {
        filteredAlerts = filteredAlerts.filter(alert =>
            selectedParadaNoAuth ? alert.tipo_alerta === selectedParadaNoAuth && alert.id_vehiculo === selectedAlertType
                : alert.id_vehiculo === selectedAlertType
        );
    }

    // Agrupar datos por vehículo y calcular el porcentaje
    let groupedAlerts = filteredAlerts.reduce((acc, record) => {

        //Obtiene identificador del vehiculo
        const key = record.id_vehiculo;

        //Crea los parametros del agrupado
        if (!acc[key]) {
            acc[key] = {
                ...record, children: [], totalDetenedio: 0, totalApadado: 0, markers: [], //Marcadores del mapa 
            };
        }
        // Suma correctamente las alertas según su tipo
        if (record.tipo_alerta === "6091ba3e662e2b2e74500a43") { // detenido
            acc[key].totalDetenedio += 1;
        }

        if (record.tipo_alerta === "6761c5a6c3e4e161ae6ea34b") { // Apagado
            acc[key].totalApadado += 1;
        }

        acc[key].fecha = '';
        acc[key].hora = '';
        acc[key].geocerca = '';
        acc[key].isMaster = true;
        acc[key].children.push(record);
        acc[key].markers.push({
            lat: parseFloat(record.latitud),
            lng: parseFloat(record.longitud),
            tipo_alerta: record.tipo_alerta === "6091ba3e662e2b2e74500a43" ? "D" : "A"
        });

        return acc;
    }, {});

    const formattedData = Object.values(groupedAlerts).map((item, index) => ({
        ...item, detenido: 'Detenidos: ' + parseFloat(item.totalDetenedio).toString() + ' Eventos',
        apagado: 'Apagados: ' + parseFloat(item.totalApadado).toString() + ' Eventos', markers: item.markers,
    }));

    return (
        <div>
            <div className="m-3">
                <Row>
                    <Col md={10}>
                        <h4 className="mt-2">Paradas no autorizadas</h4>
                    </Col>
                </Row>
            </div>

            <Card className="p-3 mb-3 mt-3">

                {/* Titulo principal de la pagina */}
                <hr className="text-danger" />

                <Filtros onGenerateReport={handleGenerateReport} imprimir={filters.uids_vehiculo.length == 0 ? (false) : isLoading ? (false) : (formattedData.length > 0 ? true : false)} />

                {/* Indicador de progreso de carga */}
                {paginadorAlertas.cargando && (
                    // <Col md={12} className="mt-3">
                    //     <Row>
                    //         <Col
                    //             xs={6}
                    //             sm="auto"
                    //             as={Flex}
                    //             alignItems="center"
                    //             className="pe-2 small py-2"
                    //             key={'Generando reporte...'}
                    //             style={{ fontWeight: 'bold' }}
                    //         >
                    //             <span
                    //                 className="dot"
                    //                 style={{
                    //                     backgroundColor: "#2770e9",
                    //                     width: 10,
                    //                     height: 10,
                    //                     borderRadius: '50%',
                    //                     display: 'inline-block',
                    //                     marginRight: 5
                    //                 }}
                    //             ></span>
                    //             <span>Generando reporte...</span>
                    //             <span className="ms-1">
                    //                 ({`${(totalCarga.length > 0 ? totalCarga[0].percent : 0).toFixed(0)}%`})
                    //             </span>
                    //         </Col>
                    //     </Row>
                    //     <ProgressBar
                    //         style={{ height: 10 }}
                    //         className="shadow-none rounded-4 mb-3"
                    //     >
                    //         {totalCarga.map((item) => (
                    //             <ProgressBar
                    //                 style={{ backgroundColor: item.color }}
                    //                 now={item.percent}
                    //                 key={item.title}
                    //                 animated={item.animated}
                    //                 className={classNames('overflow-visible position-relative rounded-pill')}
                    //             />
                    //         ))}
                    //     </ProgressBar>
                    // </Col>
                    <Row>
                        <Col md={12}>
                            <StandardProgressBar
                                calculateTotalProgress={calculateTotalProgress}
                                debugMetrics={debugMetrics}
                            />
                        </Col>
                    </Row>
                )}
            </Card>

            {/* Modificar la lógica del spinner para mostrar solo cuando no hay progreso */}
            {filters.uids_vehiculo.length == 0 ? (
                // Estado inicial - no se ha filtrado
                <WelcomeEmptyState reportType="paradas no autorizadas" />
            ) : isLoading ? (
                <>
                    <LoadingEmptyState reportType="paradas no autorizadas" />
                </>
            ) : formattedData.length === 0 ? (
                <>
                    <NoDataEmptyState reportType="paradas no autorizadas" entityType='paradas no autorizadas' />
                </>
            ) : (
                <div id="reporte-paradas-content" data-report-area="paradas-no-autorizadas">
                    <Row className="g-3 mt-0 mb-3">
                        <Col md={6} className=" mt-0">
                            <div id="card-detail-paradas" style={{ height: '400px' }}>
                                <ParadasNoAutorizadas
                                    onSelectParadaNoAuth={handleSelectParadaNoAuth}
                                    highlightedParadaNoAuth={highlightedParadaNoAuth}
                                    data={alerts}
                                    handleFiltro={handleClearFiltersNoAuth}
                                />
                            </div>
                        </Col>
                        <Col md={6} className="mt-0">
                            <div id="card-paradas-vehiculo" style={{ height: '400px' }}>
                                <ParadasNoAutorizadasVehiculo
                                    data={filteredAlertsByEvent.map(alert => ({
                                        ...alert,
                                        color: getAlertColor(alert.id_vehiculo) // Color persistente por vehiculo
                                    }))}
                                    onSelectAlertType={handleSelectAlertType}
                                    highlightedAlertType={highlightedAlertType}
                                    handleFiltro={handleClearFilters}
                                    highlightedParadaNoAuth={selectedTitleParadaNoAuth}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row className="g-3">
                        <Col md={12}>
                            <div id="tabla-paradas">
                                <NoAutorizadosMasterDetailList
                                    data={formattedData}
                                    title="Listado de Paradas No Autorizadas"
                                    isGroup={true}
                                    searchValue={searchValue}
                                    onSearch={setSearchValue}
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
}
